// public/flags: one SVG per state that has an openly licensed flag, listed in index.json (written
// by tools/flags/fetch-flags.mjs). The manifest is optional: without it no state has a flag and
// the UI keeps its emblems.

import type { Source } from '../core/schema.ts';
import { makeReader, type Reader } from './fetch.ts';
import type { Fetcher, FlagEntry, Flags } from './types.ts';

const FOLDER = 'flags';
const MANIFEST = 'index.json';
const DAY = /^\d{4}-\d{2}-\d{2}$/;

interface RawManifest {
  files?: Record<string, Partial<Record<keyof FlagEntry, unknown>>>;
  sources?: Record<string, Source>;
}

/** The folder beside the data folder: ('/data', 'flags') → '/flags'. */
export function siblingUrl(baseUrl: string, name: string): string {
  const base = baseUrl.replace(/\/+$/, '');
  const cut = base.lastIndexOf('/');
  return cut < 0 ? name : `${base.slice(0, cut)}/${name}`;
}

/** Why an entry cannot be used, or null. An undated flag would show before its design existed. */
function problemOf(e: Partial<Record<keyof FlagEntry, unknown>> | null, sources: Record<string, Source>): string | null {
  if (!e || typeof e.from !== 'string' || !DAY.test(e.from)) {
    return 'has no adoption date (from, YYYY-MM-DD)';
  }
  if (e.to !== undefined && (typeof e.to !== 'string' || !DAY.test(e.to) || e.to <= e.from)) {
    return `has an end (${String(e.to)}) that is not a date after ${e.from}`;
  }
  if (typeof e.source !== 'string' || !sources[e.source]) {
    return `cites source ${String(e.source)}, which the manifest does not define`;
  }
  return null;
}

/** Fetch index.json of the flags folder next to `baseUrl`; null (with a warning) when unusable. */
export async function loadFlags(baseUrl: string, fetcher: Fetcher | undefined, main: Reader): Promise<Flags | null> {
  const base = siblingUrl(baseUrl, FOLDER);
  const reader = makeReader(base, fetcher);
  const manifest = await reader.json<RawManifest>(MANIFEST);
  main.warnings.push(...reader.warnings.map((w) => w.replace(`data: ${MANIFEST}`, `data: ${FOLDER}/${MANIFEST}`)));
  if (!manifest) {
    return null;
  }

  if (!manifest.files || typeof manifest.files !== 'object') {
    const msg = `data: ${FOLDER}/${MANIFEST} lists no files`;
    main.warnings.push(msg);
    console.warn(msg);
    return null;
  }

  // An entry that fails is dropped, with a warning: no flag beats a wrongly dated one.
  const sources = manifest.sources ?? {};
  const files = new Map<string, FlagEntry>();
  for (const [iso3, e] of Object.entries(manifest.files)) {
    const problem = problemOf(e, sources);
    if (problem) {
      const msg = `data: ${FOLDER}/${MANIFEST} ${iso3} ${problem}`;
      main.warnings.push(msg);
      console.warn(msg);
      continue;
    }
    files.set(iso3, { from: e.from as string, ...(e.to === undefined ? {} : { to: e.to as string }), source: e.source as string });
  }
  return { base, files, sources: Object.values(sources) };
}
